import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components/keenicons';
import { Can } from '@casl/react';
import { useAbility } from '@/providers/';
import { useAuthContext } from '@/auth';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { createDataTableOptions, createDataTableConfig } from '@/utils/dataTableConfig';
import { getAccessToken } from '@/utils/LocalStorage';
import { renderMultiAvatars } from '@/utils/renderMultiAvatars';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import letterTemplateService from '@/services/letters/letterTemplateService';

DataTable.use(DT);

/**
 * Firm-wide letter templates (Documents → Letter Templates).
 */
const LetterTemplates = () => {
  const { logout } = useAuthContext();
  const { ability } = useAbility();
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const filterRef = useRef({});
  const token = getAccessToken();
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const canManage = ability.can('handle', 'letter-template');
  const canDelete = ability.can('delete', 'letter-template');

  const refresh = () => tableRef.current?.dt()?.ajax.reload();

  const columns = [
    { title: 'Name', name: 'name', data: 'name', render: (v) => `<span class="link cursor-pointer">${(v)}</span>` },
    { title: 'Practice area', name: 'record_type', data: null, render: (d, t, row) => (row.record_type?.name || 'Any matter') },
    { title: 'Jurisdiction', name: 'jurisdiction', data: 'jurisdiction', render: (v) => v || '-' },
    { title: 'Days to respond', name: 'default_days_to_respond', data: 'default_days_to_respond' },
    {
      title: 'Active', name: 'is_active', data: 'is_active',
      render: (v) => `<span class="badge badge-pill badge-outline ${v ? 'badge-success' : 'badge-light'}">${v ? 'Active' : 'Inactive'}</span>`,
    },
    { title: 'Created By', name: 'created_by', data: 'created_by', render: (d, t, row) => `<div id="template-created-by-${row.id}"></div>` },
    { title: 'Updated', name: 'updated_at', data: 'updated_at' },
    { title: '', data: null, orderable: false, searchable: false, render: (d, t, row) => `<div id="template-action-${row.id}"></div>` },
  ];

  const handleDuplicate = async (id) => {
    try {
      const { status, data } = await letterTemplateService.duplicate(id);
      if (status) {
        toast.success('Template duplicated.');
        navigate(`/documents/letter-templates/${data.id}`);
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not duplicate the template.');
    }
  };

  const handleDelete = async () => {
    const id = actionId;
    closeModal();
    try {
      const { status } = await letterTemplateService.remove(id);
      if (status) {
        refresh();
        toast.success('Template deleted.');
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not delete the template.');
    }
  };

  const renderActions = (row) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="text-gray-600"><KeenIcon icon="dots-vertical" className="text-[18px]" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={() => navigate(`/documents/letter-templates/${row.id}`)}>
          <KeenIcon icon={canManage ? 'notepad-edit' : 'eye'} className="mr-0.5 h-4 w-4" /> {canManage ? 'Edit' : 'View'}
        </DropdownMenuItem>
        {canManage && (
          <DropdownMenuItem onClick={() => handleDuplicate(row.id)}>
            <KeenIcon icon="copy" className="mr-0.5 h-4 w-4" /> Duplicate
          </DropdownMenuItem>
        )}
        {canDelete && (
          <DropdownMenuItem onClick={() => openModal(row.id)} className="text-red-600">
            <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ajaxConfig = createDataTableConfig('/letter-templates/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[0, 'asc']],
    drawCallback: function () {
      const api = this.api();
      api.rows({ page: 'current' }).data().each((row) => {
        const container = document.getElementById(`template-action-${row.id}`);
        if (container) {
          if (!container._reactRoot) container._reactRoot = createRoot(container);
          container._reactRoot.render(renderActions(row));
        }
        const createdBy = document.getElementById(`template-created-by-${row.id}`);
        if (createdBy) {
          if (!createdBy._reactRoot) createdBy._reactRoot = createRoot(createdBy);
          createdBy._reactRoot.render(renderMultiAvatars(row.created_by));
        }
      });
      api.rows({ page: 'current' }).every(function () {
        const row = this.data();
        const cell = this.node()?.querySelector('td .link');
        if (cell && !cell._bound) {
          cell._bound = true;
          cell.addEventListener('click', () => navigate(`/documents/letter-templates/${row.id}`));
        }
      });
    },
  });

  return (
    <Container>
      <div className="card min-w-full mb-4">
        <div className="card-header">
          <div>
            <h3 className="card-title">Letter Templates</h3>
            <p className="text-xs text-gray-500 mt-0.5">Firm templates with merge fields. Demand letters are generated from these on a matter.</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <Can I="handle" a="letter-template" ability={ability}>
              <button className="text-xs btn btn-sm btn-primary" onClick={() => navigate('/documents/letter-templates/create')}>
                <KeenIcon icon="plus" className="w-fit btn-icon" /> New template
              </button>
            </Can>
          </div>
        </div>

        <DataTable id="letter-templates-table" ajax={ajaxConfig} columns={columns} options={options} ref={tableRef} />
      </div>

      <ConfirmationModal
        show={isOpen}
        onClose={closeModal}
        onConfirm={handleDelete}
        title="Delete this template?"
        message="Letters already generated from it are not affected."
      />
    </Container>
  );
};

export { LetterTemplates };
